import { Injectable, computed, signal } from '@angular/core';

export enum ToastType {
  Info = 'info',
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
}

export interface ToastRequest {
  message: string;
  title?: string | null;
  type?: ToastType;
  dismissible?: boolean;
  duration?: number | null;
}

export interface ToastMessageInstance {
  id: number;
  title: string | null;
  message: string;
  type: ToastType;
  dismissible: boolean;
  duration: number | null;
  visible: boolean;
}

@Injectable({ providedIn: 'root' })
export class ToastMessageService {
  readonly ToastType = ToastType;
  readonly toasts = signal<ToastMessageInstance[]>([]);
  private readonly dismissTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly removalTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private idCounter = 0;
  private readonly transitionMs = 240;

  show(request: ToastRequest): number {
    const id = ++this.idCounter;
    const duration = request.duration ?? 5000;
    const toast: ToastMessageInstance = {
      id,
      title: request.title ?? null,
      message: request.message,
      type: request.type ?? ToastType.Info,
      dismissible: request.dismissible ?? true,
      duration: duration && duration > 0 ? duration : null,
      visible: false,
    };

    this.toasts.update((current) => [toast, ...current]);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.hasToast(id)) return;
        this.setVisible(id, true);
      });
    });

    if (toast.duration) {
      this.setDismissTimer(id, toast.duration);
    }

    return id;
  }

  success(message: string, title?: string | null, duration?: number | null): number {
    return this.show({ message, title, type: ToastType.Success, duration });
  }

  info(message: string, title?: string | null, duration?: number | null): number {
    return this.show({ message, title, type: ToastType.Info, duration });
  }

  warning(message: string, title?: string | null, duration?: number | null): number {
    return this.show({ message, title, type: ToastType.Warning, duration });
  }

  error(message: string, title?: string | null, duration?: number | null): number {
    return this.show({ message, title, type: ToastType.Error, duration });
  }

  dismiss(id: number): void {
    if (!this.hasToast(id)) return;
    this.clearDismissTimer(id);
    this.clearRemovalTimer(id);
    this.setVisible(id, false);
    this.removalTimers.set(
      id,
      setTimeout(() => this.removeToast(id), this.transitionMs)
    );
  }

  reset(): void {
    this.dismissTimers.forEach((timer) => clearTimeout(timer));
    this.removalTimers.forEach((timer) => clearTimeout(timer));
    this.dismissTimers.clear();
    this.removalTimers.clear();
    this.toasts.set([]);
  }

  private setVisible(id: number, visible: boolean): void {
    let found = false;
    this.toasts.update((current) =>
      current.map((toast) => {
        if (toast.id !== id) return toast;
        found = true;
        return { ...toast, visible };
      })
    );
    if (!found && !visible) {
      this.removeToast(id);
    }
  }

  private removeToast(id: number): void {
    this.clearDismissTimer(id);
    this.clearRemovalTimer(id);
    this.toasts.update((current) => current.filter((toast) => toast.id !== id));
  }

  private hasToast(id: number): boolean {
    return this.toasts().some((toast) => toast.id === id);
  }

  private setDismissTimer(id: number, duration: number): void {
    this.clearDismissTimer(id);
    this.dismissTimers.set(
      id,
      setTimeout(() => this.dismiss(id), duration)
    );
  }

  private clearDismissTimer(id: number): void {
    const timer = this.dismissTimers.get(id);
    if (!timer) return;
    clearTimeout(timer);
    this.dismissTimers.delete(id);
  }

  private clearRemovalTimer(id: number): void {
    const timer = this.removalTimers.get(id);
    if (!timer) return;
    clearTimeout(timer);
    this.removalTimers.delete(id);
  }
}
